import { useCallback, useMemo, useRef, useState } from 'react';
import { Loader2, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { CashCountPanel } from '@/components/pos/CashCountPanel';
import { useCloseShift, useCurrentShift, useShiftSummary } from '@/hooks/queries';
import { formatTime, rupiah, todayISO } from '@/lib/format';
import { expectedDrawerCash, shiftTenderRows } from '@/lib/shiftTenders';
import type { Shift } from '@/types/sale';

/** The backend caps the close note at 500 chars (`ShiftService.closeShift`). */
const NOTE_MAX = 500;

/**
 * Count the drawer and close the open shift: the shift banner, the cash count,
 * the variance reason, and "Tutup shift". Rendered by the close-shift page
 * (`/shift/close`), which Beranda and the Shift page link to. Renders nothing
 * without an open shift.
 */
export function CloseShiftSection({ onClosed }: { onClosed?: (shift: Shift) => void }) {
  const { data: current } = useCurrentShift();
  const { data: summary } = useShiftSummary(todayISO());
  const closeMut = useCloseShift();

  // The counted drawer lives in STATE, not only in a ref, because the variance
  // it implies decides whether the reason field is on screen. The denominations
  // stay in a ref: nothing renders off them, and a fresh object every keystroke
  // would feed the identity loop described on `handleCount` below.
  const [countedCash, setCountedCash] = useState(0);
  const denomsRef = useRef<Record<string, number>>({});
  const [note, setNote] = useState('');

  const tenders = useMemo(() => shiftTenderRows(summary?.byMethod), [summary?.byMethod]);
  // While a shift is open, the drawer should also hold the opening float.
  const openingFloat = current?.openingFloat ?? 0;
  const expectedCash = expectedDrawerCash(tenders, openingFloat);
  const variance = countedCash - expectedCash;
  // `CashCountPanel` shows the variance as "—" until at least one note or coin
  // is entered, so the reason field holds to the same threshold — asking why a
  // drawer is short before anyone has counted it would contradict the panel
  // directly above it. A positive count is exactly "counting started": every
  // denomination is positive, so any entry makes the sum positive.
  const counting = countedCash > 0;
  const needsReason = counting && variance !== 0;

  // `useCallback` with no deps is load-bearing, not tidiness: `CashCountPanel`
  // lists `onChange` in the effect deps that emits the count, so a new closure
  // each render would re-emit, re-set state, re-render — forever. Stable
  // identity plus a number-valued setState lets React bail out instead.
  const handleCount = useCallback((counted: number, denoms: Record<string, number>) => {
    denomsRef.current = denoms;
    setCountedCash(counted);
  }, []);

  const handleClose = () => {
    const trimmed = note.trim();
    closeMut.mutate(
      {
        countedCash,
        denominations: denomsRef.current,
        // Only a real discrepancy carries a reason. A note typed before the
        // count came back into balance would otherwise be filed against a
        // variance of zero, which reads as a discrepancy that never happened.
        note: needsReason && trimmed ? trimmed : null,
      },
      {
        onSuccess: (shift) => {
          const v = shift.variance ?? 0;
          const detail =
            v === 0 ? 'Kas sesuai.' : v > 0 ? `Lebih ${rupiah(v)}.` : `Kurang ${rupiah(Math.abs(v))}.`;
          toast.success(`Shift ditutup. ${detail}`);
          setNote('');
          onClosed?.(shift);
        },
        onError: (e: { message?: string }) => toast.error(e?.message ?? 'Gagal menutup shift.'),
      },
    );
  };

  if (!current) return null;

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-emerald-500/25 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800">
        <span className="flex items-center gap-2 font-medium">
          <Unlock className="h-4 w-4" /> Shift berjalan sejak {formatTime(current.openedAt)}
        </span>
        {openingFloat > 0 && <span className="tnum text-emerald-700">Modal awal {rupiah(openingFloat)}</span>}
      </div>

      <CashCountPanel expectedCash={expectedCash} onChange={handleCount} />

      {/* A reason for the discrepancy — the backend has always accepted
          `note` and stored it on the closed shift, but the POS never
          sent one, so a variance was recorded with no explanation
          anywhere (#1749). Shown only when the drawer is actually off:
          a shift that balances gains no extra field to fill in. */}
      {needsReason && (
        <div className="rounded-2xl border border-gold-500/40 bg-gold-200/30 px-5 py-4">
          <label
            htmlFor="shift-variance-note"
            className="flex flex-wrap items-baseline gap-x-2 text-xs font-semibold uppercase tracking-wide text-ink-600"
          >
            Alasan selisih
            <span className="tnum font-normal normal-case text-ink-400">
              {variance > 0 ? 'Lebih' : 'Kurang'} {rupiah(Math.abs(variance))}
            </span>
          </label>
          <textarea
            id="shift-variance-note"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
            maxLength={NOTE_MAX}
            rows={2}
            placeholder="Mis. kembalian kurang bayar, uang palsu ditolak, selisih pecahan koin…"
            className="mt-2 w-full resize-none rounded-lg border border-cream-200 bg-white px-3 py-2 text-sm text-ink-900 outline-none placeholder:text-ink-400/70 focus:border-gold-500"
          />
          <p className="mt-1.5 text-xs text-ink-400">
            Tercatat pada shift yang ditutup. Kosongkan jika penyebabnya belum diketahui.
          </p>
        </div>
      )}

      <Button className="w-full gap-2" onClick={handleClose} disabled={closeMut.isPending}>
        {closeMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
        Tutup shift
      </Button>
    </>
  );
}
